import { Injectable, inject, computed, Signal } from '@angular/core';
import { WidgetLayoutService } from '@fi-shared/widget/widget-layout.service';
import { IOnboardingActive, IOnboardingActiveData } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingDisplayType, FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';
import { MSPerDay } from '@fi-shared/fi-format-date/fi-format-date.service';

interface IIsShownAtPlaceholderArgs {
    layoutId: string;
    placeholderIndex: number;
    isHiddenFromHomePage: boolean;
    isAtSideWidgets: boolean;
}

@Injectable()
export class FiOnboardingHomePageService {
    private widgetLayoutService = inject(WidgetLayoutService);
    private fiOnboardingActiveDataService = inject(FiOnboardingActiveDataService);

    isAtSideWidgets = computed<boolean>(
        () => !!this.visibleOnboardings().find((onboarding) => onboarding.IsAtSideWidgets),
    );

    visibleOnboardings = computed<IOnboardingActive[]>(() => {
        const visibleOnboardings =
            this.onboardingActiveData()?.ActiveOnboardings.filter((onboarding) => {
                if (onboarding.IsHiddenFromHomePage) {
                    return false;
                }
                if (onboarding.Status === FiOnboardingStatusType.Completed) {
                    const now = new Date();
                    return now.getTime() - new Date(onboarding.CompletionEndDate).getTime() < MSPerDay;
                }
                return true;
            }) ?? [];
        return visibleOnboardings /*.sort((item) => item.CompletedFraction)*/;
    });

    readonly onboardingActiveData: Signal<IOnboardingActiveData> =
        this.fiOnboardingActiveDataService.onboardingActiveData;

    public isShownAtPlaceholder(args: IIsShownAtPlaceholderArgs): boolean {
        if (args.isHiddenFromHomePage) {
            return false;
        }
        const layouts = this.widgetLayoutService.Layouts;

        if ([layouts.OneColumn.Id].includes(args.layoutId)) {
            return args.placeholderIndex === 0;
        }

        if (
            [
                layouts.TwoColumnsEquals.Id,
                layouts.TwoColumnsLeftSide.Id,
                layouts.TwoColumnsRightSide.Id,
                layouts.TwoColumnsEqualsWideBottom.Id,
                layouts.TwoColumnsLeftSideWideBottom.Id,
                layouts.TwoColumnsRightSideWideBottom.Id,
            ].includes(args.layoutId)
        ) {
            return !args.isAtSideWidgets ? args.placeholderIndex === 0 : args.placeholderIndex === 1;
        }

        if (
            [
                layouts.TwoColumnsEqualsWideTop.Id,
                layouts.TwoColumnsLeftSideWideTop.Id,
                layouts.TwoColumnsRightSideWideTop.Id,
            ].includes(args.layoutId)
        ) {
            return !args.isAtSideWidgets ? args.placeholderIndex === 1 : args.placeholderIndex === 2;
        }

        if ([layouts.ThreeColumnsEquals.Id, layouts.ThreeColumnsRightSide.Id].includes(args.layoutId)) {
            return !args.isAtSideWidgets ? args.placeholderIndex === 0 : args.placeholderIndex === 2;
        }

        if ([layouts.ThreeColumnsEqualsWideTop.Id].includes(args.layoutId)) {
            return !args.isAtSideWidgets ? args.placeholderIndex === 1 : args.placeholderIndex === 3;
        }

        throw new Error(`isShownAtPlaceholder: unknown layout id ${args.layoutId}`);
    }
}
